import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { fetchCMS } from "@/lib/cms";

const PRODUCTS_FALLBACK = [
  { tag: "Flagship", title: "PVC Decor Film", subtitle: "For Membrane Doors, Louvers, Shutters & Wall Panels", process: "Vacuum Press Process", units: "sq mtr · running mtr · roll", points: ["3D wrap ready", "Scratch & moisture resistant", "Wide design library", "Custom print runs"], image: "/images/pvc-decor-film.jpg", accent: "bg-orange-600" },
  { tag: "Core", title: "PETLAM", subtitle: "PET Laminated Decor Film for ACP, Partition & Cladding Panels", process: "Laminated & Coated", units: "kg", points: ["Consistent gsm & gloss", "Wide colour & texture range", "Bonds cleanly to ACP core", "Custom roll widths"], image: "/images/walnut-texture.jpg", accent: "bg-slate-900" },
];

export default function Products() {
  const [products, setProducts] = useState(PRODUCTS_FALLBACK);

  useEffect(() => {
    fetchCMS("products").then((items) => {
      if (items && items.length > 0) {
        setProducts(items.filter(p => p.active !== false).map((p, i) => ({
          tag: p.tag || "Product",
          title: p.name || p.title || "Product",
          subtitle: p.subtitle || "",
          process: p.process || "",
          points: typeof p.points === "string" ? p.points.split("|") : (p.points || []),
          image: p.image || "/images/pvc-decor-film.jpg",
          units: p.units || p.unit_of_sale || "",
          accent: i === 0 ? "bg-orange-600" : "bg-slate-900",
        })));
      }
    });
  }, []);

  return (
    <section id="products" data-testid="products-section" className="py-20 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline">01 &mdash; Product Line</div>
            <h2 className="mt-3 font-display font-bold text-slate-900 text-4xl sm:text-5xl tracking-tight">
              Two products.<br />One engineered standard.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base leading-relaxed">
            PVC Decor Film and PETLAM. TopDecor is built to replace imports with consistent, local supply.
          </p>
        </div>

        {/* Product names strip */}
        <div className="flex flex-wrap items-center gap-3 mb-10">
          {products.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-2.5">
              {i > 0 && <span className="w-1 h-1 bg-orange-500 rotate-45 shrink-0" />}
              <span className="text-sm font-display font-semibold text-slate-700 uppercase tracking-wide">
                {p.title}
              </span>
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <article
              key={i}
              data-testid={`product-card-${i}`}
              className="card-lift group relative border border-slate-200 bg-white flex flex-col overflow-hidden"
            >
              <div className="relative h-64 overflow-hidden bg-slate-100">
                <img
                  src={p.image}
                  alt={p.title}
                  width={800}
                  height={512}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-4 left-4 ${p.accent} text-white text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5`}>
                  {p.tag}
                </span>
              </div>

              <div className="p-7 flex-1 flex flex-col">
                <div className="text-xs uppercase tracking-widest text-slate-500">{p.process}</div>
                <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">{p.title}</h3>
                <p className="text-slate-600 text-sm mt-1">{p.subtitle}</p>
                {p.units && (
                  <div className="mt-2 inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-[0.16em] font-bold text-orange-700 bg-orange-50 border border-orange-200 px-2.5 py-1">
                    Sold by: {p.units}
                  </div>
                )}

                <ul className="mt-5 space-y-2 flex-1">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="w-1 h-1 bg-orange-600 mt-2 shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 border-b border-slate-900 self-start pb-0.5 hover:text-orange-600 hover:border-orange-600 transition-colors"
                >
                  Request this product
                  <ArrowRight size={14} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
